import React, { useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [statusMessage, setStatusMessage] = useState('');

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) return 'Name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters';
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) return 'Name can only contain letters and spaces';
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validateMobile = (mobile) => {
    if (!mobile.trim()) return 'Mobile number is required';
    const mobileRegex = /^[\+]?[1-9][\d]{9,14}$/;
    if (!mobileRegex.test(mobile.replace(/\s/g, ''))) return 'Please enter a valid mobile number';
    return '';
  };

  const validateMessage = (message) => {
    if (!message.trim()) return 'Message is required';
    if (message.trim().length < 10) return 'Message must be at least 10 characters';
    if (message.trim().length > 500) return 'Message cannot exceed 500 characters';
    return '';
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return validateName(value);
      case 'email':
        return validateEmail(value);
      case 'mobile':
        return validateMobile(value);
      case 'message':
        return validateMessage(value);
      default:
        return '';
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear any existing error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear status messages when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
      setStatusMessage('');
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    const error = validateField(name, value);
    
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    newErrors.name = validateName(formData.name);
    newErrors.email = validateEmail(formData.email);
    newErrors.mobile = validateMobile(formData.mobile);
    newErrors.message = validateMessage(formData.message);

    setErrors(newErrors);
    
    return !Object.values(newErrors).some(error => error !== '');
  };

  const contactSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      setStatusMessage('Please fix the errors above before submitting.');
      return;
    }

    setIsLoading(true);
    setSubmitStatus(null);
    setStatusMessage('');

    try {
      const now = DateTime.now();
      const formattedDate = now.toFormat('dd-MM-yyyy HH:mm:ss');
      
      const contact = {
        name: formData.name.trim(),
        mobile: formData.mobile.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
        time: formattedDate,
      };

      await axios.post("/contacts.json", contact);
      
      setSubmitStatus('success');
      setStatusMessage('Thank you! Your message has been sent successfully. We\'ll get back to you soon.');
      
      // Reset form
      setFormData({
        name: '',
        mobile: '',
        email: '',
        message: ''
      });
      setErrors({});
      
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error');
      setStatusMessage('Sorry, there was an error sending your message. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldClass = (fieldName) => {
    let baseClass = fieldName === 'message' ? 'form-textarea' : 'form-input';
    if (errors[fieldName]) baseClass += ' error';
    else if (formData[fieldName] && !errors[fieldName]) baseClass += ' success';
    return baseClass;
  };

  const getGroupClass = (fieldName) => {
    let baseClass = 'form-group';
    if (errors[fieldName]) baseClass += ' has-error';
    else if (formData[fieldName] && !errors[fieldName]) baseClass += ' has-success';
    return baseClass;
  };

  return (
    <div className="contact-page">
      <div className="side-bar" />
      <div className="contact-wrapper">
        <div className="contact-container">
          <div className="contact-header">
            <h1>Get In Touch</h1>
            <p>We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
          </div>

          {submitStatus && (
            <div className={`form-status ${submitStatus}`}>
              {submitStatus === 'success' ? (
                <span>✓</span>
              ) : (
                <span>⚠</span>
              )}
              {statusMessage}
            </div>
          )}

          <form onSubmit={contactSubmit} className="contact-form" noValidate>
            <div className={getGroupClass('name')}>
              <label htmlFor="name" className="form-label">
                Full Name *
              </label>
              <div className="form-input-wrapper">
                <input
                  id="name"
                  name="name"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={formData.name}
                  placeholder="Enter your full name"
                  className={getFieldClass('name')}
                  required
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                <span className="input-icon">👤</span>
              </div>
              {errors.name && (
                <div id="name-error" className="error-message">
                  <span>⚠</span>
                  {errors.name}
                </div>
              )}
            </div>

            <div className={getGroupClass('mobile')}>
              <label htmlFor="mobile" className="form-label">
                Mobile Number *
              </label>
              <div className="form-input-wrapper">
                <input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={formData.mobile}
                  placeholder="Enter your mobile number"
                  className={getFieldClass('mobile')}
                  required
                  aria-describedby={errors.mobile ? 'mobile-error' : undefined}
                />
                <span className="input-icon">📱</span>
              </div>
              {errors.mobile && (
                <div id="mobile-error" className="error-message">
                  <span>⚠</span>
                  {errors.mobile}
                </div>
              )}
            </div>

            <div className={getGroupClass('email')}>
              <label htmlFor="email" className="form-label">
                Email Address *
              </label>
              <div className="form-input-wrapper">
                <input
                  id="email"
                  name="email"
                  type="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={formData.email}
                  placeholder="Enter your email address"
                  className={getFieldClass('email')}
                  required
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                <span className="input-icon">📧</span>
              </div>
              {errors.email && (
                <div id="email-error" className="error-message">
                  <span>⚠</span>
                  {errors.email}
                </div>
              )}
            </div>

            <div className={getGroupClass('message')}>
              <label htmlFor="message" className="form-label">
                Your Message *
              </label>
              <textarea
                id="message"
                name="message"
                onChange={handleChange}
                onBlur={handleBlur}
                value={formData.message}
                placeholder="Tell us how we can help you..."
                className={getFieldClass('message')}
                required
                aria-describedby={errors.message ? 'message-error' : undefined}
              />
              {errors.message && (
                <div id="message-error" className="error-message">
                  <span>⚠</span>
                  {errors.message}
                </div>
              )}
              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
                {formData.message.length}/500 characters
              </div>
            </div>

            <button
              type="submit"
              className={`submit-button ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Message'}
              {isLoading && <div className="loading-spinner"></div>}
            </button>
          </form>
        </div>
      </div>
      <div className="side-bar" />
    </div>
  );
};

export default Contact;